import React,{useLayoutEffect,useMemo,useRef,useState,type CSSProperties} from 'react';
import {useCurrentFrame,useDelayRender,useRemotionEnvironment,useVideoConfig} from 'remotion';
import {BrowserRenderMediaAudio} from './browser-render-media';
import {sourceMediaRequest} from './media-request';
import {drawAlphaVideoFrame} from './browser-alpha-video-clock';

const FRAME_TIMEOUT_MS=15_000;

/** Browser export only. A detached native video preserves the alpha channel in drawImage. */
export function BrowserAlphaVideo({src,style,trimBefore,playbackRate,muted,hasAudioTrack,volume,isCaptionClockSource,declaredPixels}:{
  src:string;style:CSSProperties;trimBefore:number;playbackRate:number;muted:boolean;
  hasAudioTrack:boolean;volume:(frame:number)=>number;isCaptionClockSource?:boolean;declaredPixels:number;
}){
  const frame=useCurrentFrame(),{fps}=useVideoConfig(),delay=useDelayRender();
  const environment=useRemotionEnvironment();
  const canvas=useRef<HTMLCanvasElement>(null),video=useRef<HTMLVideoElement|null>(null);
  const completion=useRef<{key:object;finish:()=>void}|null>(null);
  const [ready,setReady]=useState<object|null>(null),[error,setError]=useState<Error|null>(null);
  const key=useMemo(()=>({src,frame,fps,trimBefore,playbackRate,declaredPixels}),[src,frame,fps,trimBefore,playbackRate,declaredPixels]);

  useLayoutEffect(()=>{
    const element=document.createElement('video');
    element.crossOrigin='anonymous';
    element.muted=true;
    element.preload='auto';
    element.playsInline=true;
    video.current=element;
    element.src=src;
    element.load();
    return()=>{
      if(video.current===element)video.current=null;
      element.pause();element.removeAttribute('src');element.load();
    };
  },[src]);

  useLayoutEffect(()=>{
    const element=video.current;
    if(!element)return;
    let active=true,finished=false;
    const controller=new AbortController();
    const handle=delay.delayRender('투명 영상 프레임 디코딩');
    const finish=()=>{if(finished)return;finished=true;clearTimeout(timer);delay.continueRender(handle);};
    const fail=(reason:unknown)=>{
      if(!active||video.current!==element)return;
      const failure=reason instanceof Error?reason:Error('투명 영상 프레임을 만들지 못했습니다.');
      setError(failure);
      if(environment.isClientSideRendering||environment.isRendering)delay.cancelRender(failure);
      finish();
    };
    const timer=setTimeout(()=>controller.abort(Error('투명 영상 디코딩 시간이 초과됐습니다. 원본 파일을 확인해 주세요.')),FRAME_TIMEOUT_MS);
    const current=()=>active&&!controller.signal.aborted&&video.current===element;
    completion.current={key,finish};
    setReady(null);
    void (async()=>{
      const output=canvas.current;
      if(!output)throw Error('투명 영상 출력 화면을 만들지 못했습니다.');
      await drawAlphaVideoFrame(element,output,{frame,fps,trimBefore,playbackRate},controller.signal,declaredPixels);
      if(!current())return;
      setReady(key);
    })().catch(fail);
    return()=>{
      active=false;controller.abort();
      if(completion.current?.key===key)completion.current=null;
      finish();
    };
  },[key]);

  // Keep the scoped delay until React has committed the canvas for this frame.
  useLayoutEffect(()=>{
    if(ready===key&&completion.current?.key===key){completion.current.finish();completion.current=null;}
  },[ready,key]);
  if(error)throw error;
  return <>
    <canvas ref={canvas} style={{...style,visibility:ready===key?'visible':'hidden'}}
      data-editor-caption-clock-source={isCaptionClockSource?'true':undefined}/>
    {hasAudioTrack&&!muted&&<BrowserRenderMediaAudio src={src} requestInit={sourceMediaRequest}
      volume={volume} trimBefore={trimBefore} playbackRate={playbackRate}/>}
  </>;
}
